// AB3 Academy — separate countdown chip, изолирует setInterval(1s) от родителя.
// Banner сам тяжёлый (DOM, кнопки) — пере-рендерить его раз в секунду ради чисел
// расточительно. Поэтому таймер живёт здесь, родитель ре-рендерится только
// когда меняется state в SandboxSessionContext (start/finish).

import React, { useEffect, useState } from 'react';
import { Clock } from 'lucide-react';

interface CountdownChipProps {
  /** ISO 8601 expiry. Если null — компонент не рендерится. */
  expiresAt: string | null;
  /** Колбэк когда remaining впервые попадает в warning-зону (<5 min). */
  onWarning?: () => void;
  /** Колбэк когда remaining = 0. */
  onExpired?: () => void;
}

const WARNING_THRESHOLD_MS = 5 * 60_000;

function formatRemaining(ms: number): string {
  if (ms <= 0) return 'Время истекло';
  const hours = Math.floor(ms / 3_600_000);
  const minutes = Math.floor((ms % 3_600_000) / 60_000);
  if (hours > 0) return `осталось ${hours} ч ${minutes} мин`;
  if (minutes > 0) return `осталось ${minutes} мин`;
  const seconds = Math.floor(ms / 1000);
  return `осталось ${seconds} сек`;
}

const CountdownChip: React.FC<CountdownChipProps> = ({ expiresAt, onWarning, onExpired }) => {
  const [remaining, setRemaining] = useState<number>(() => {
    if (!expiresAt) return 0;
    return Math.max(0, Date.parse(expiresAt) - Date.now());
  });

  useEffect(() => {
    if (!expiresAt) return undefined;
    const targetMs = Date.parse(expiresAt);
    if (Number.isNaN(targetMs)) return undefined;

    let warned = false;
    let expiredFired = false;

    const tick = () => {
      const next = Math.max(0, targetMs - Date.now());
      setRemaining(next);
      if (!warned && next > 0 && next < WARNING_THRESHOLD_MS) {
        warned = true;
        onWarning?.();
      }
      if (!expiredFired && next <= 0) {
        expiredFired = true;
        onExpired?.();
      }
    };

    tick();
    const interval = window.setInterval(tick, 1000);
    return () => window.clearInterval(interval);
  }, [expiresAt, onWarning, onExpired]);

  if (!expiresAt) return null;

  return (
    <span className="practice-banner-chip" aria-label={formatRemaining(remaining)}>
      <Clock size={12} aria-hidden />
      {formatRemaining(remaining)}
    </span>
  );
};

export default React.memo(CountdownChip);
